import { ActivatedRoute } from '@angular/router';
import { FuseConfig } from '@fuse/services/config';

/** Resolve scheme when config is set to auto. */
export function resolveScheme(
    configScheme: FuseConfig['scheme'],
    prefersDark: boolean
): 'dark' | 'light' {
    if (configScheme === 'auto') {
        return prefersDark ? 'dark' : 'light';
    }
    if (configScheme === 'dark') {
        return 'dark';
    }
    return 'light';
}

/** Walk the activated route tree to find the deepest child. */
export function deepestRoute(route: ActivatedRoute): ActivatedRoute {
    let current = route;
    while (current.firstChild) {
        current = current.firstChild;
    }
    return current;
}

const LAYOUTS = ['empty', 'classy'] as const;
type Layout = (typeof LAYOUTS)[number];

export function isLayout(value: unknown): value is Layout {
    return LAYOUTS.includes(value as Layout);
}

/**
 * Resolve the layout id from config, query param, route data, and public-route auth.
 * Pure helper — no DOM or service side effects.
 */
export function resolveLayout(options: {
    configLayout: string;
    route: ActivatedRoute;
    url: string;
    user: unknown;
}): string {
    const route = options.route;
    let layout = options.configLayout;

    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
    if (isLayout(layoutFromQueryParam)) {
        layout = layoutFromQueryParam;
    }

    for (const path of route.pathFromRoot) {
        const routeLayout = path.routeConfig?.data?.['layout'];
        if (isLayout(routeLayout)) {
            layout = routeLayout;
        }
    }

    // Public routes: force empty when unauthenticated (login shell).
    if (options.url.startsWith('/public') && !options.user) {
        return 'empty';
    }

    return layout;
}

/** Apply light/dark scheme class on body. */
export function applyScheme(body: HTMLElement, scheme: 'dark' | 'light'): void {
    body.classList.remove('light', 'dark');
    body.classList.add(scheme);
}

/** Swap theme-* classes on body for the given theme token. */
export function applyTheme(body: HTMLElement, theme: string): void {
    body.classList.forEach((className: string) => {
        if (className.startsWith('theme-')) {
            body.classList.remove(className, className.split('-')[1]);
        }
    });
    body.classList.add(theme);
}
